import {
  getWebsiteTrackingContext,
  routeTemplateForPath,
  trackWebsiteAction,
} from "@/lib/analytics/website";
import {
  hasAnalyticsConsent,
  subscribeToAnalyticsConsent,
} from "@/lib/analytics/consent";
import { parseNativeCandidatePath } from "@/lib/native/candidatePages.js";

type Audience = "hr" | "candidate";
type PageHint = { template: string; audience?: Audience };
type RageState = { count: number; startedAt: number };

const INTERACTIVE_SELECTOR = [
  "button",
  "a[href]",
  "[role='button']",
  "[role='link']",
  "[role='menuitem']",
  "input[type='button']",
  "input[type='submit']",
].join(",");
const SAFE_IDENTIFIER = /^[a-z][a-z0-9_]{0,79}$/;
const ENGAGEMENT_INTERVAL_MS = 30_000;
const ACTIVE_WINDOW_MS = 60_000;
const RAGE_WINDOW_MS = 2_000;
const DOWNLOAD_EXTENSION = /\.(?:csv|docx?|pdf|pptx?|txt|xlsx?|zip)$/i;
const CURATED_LABELS = new Map<string, string>([
  ["candidate", "candidate"],
  ["for candidates", "candidate"],
  ["recruiter", "recruiter"],
  ["recruiters", "recruiter"],
  ["for recruiters", "recruiter"],
  ["start free", "start_free"],
  ["start for free", "start_free"],
  ["get started", "start_free"],
  ["get started free", "start_free"],
  ["try for free", "start_free"],
  ["sign in", "sign_in"],
  ["log in", "sign_in"],
  ["login", "sign_in"],
  ["sign up", "sign_up"],
  ["create account", "sign_up"],
  ["create your account", "sign_up"],
  ["book demo", "book_demo"],
  ["book a demo", "book_demo"],
  ["schedule demo", "book_demo"],
  ["pricing", "pricing"],
  ["contact", "contact"],
  ["contact us", "contact"],
  ["sign up with google", "google_auth"],
  ["sign in with google", "google_auth"],
  ["continue with google", "google_auth"],
]);
const ERROR_CLASSES = new Map<string, string>([
  ["Error", "error"],
  ["TypeError", "type_error"],
  ["ReferenceError", "reference_error"],
  ["SyntaxError", "syntax_error"],
  ["RangeError", "range_error"],
  ["URIError", "uri_error"],
  ["EvalError", "eval_error"],
  ["AggregateError", "aggregate_error"],
]);

let subscribers = 0;
let installed = false;
let active = false;
let unsubscribeConsent: (() => void) | null = null;
let pageHint: PageHint | null = null;
let pageViewId = "";
let pageAudience: Audience | undefined;
let audienceEmitted: Audience | undefined;
let signupStartedEmitted = false;
let startedForms = new WeakSet<HTMLFormElement>();
let scrollMarks = new Set<number>();
let rageTargets = new Map<string, RageState>();
let scrollFrame = 0;
let engagementTimer: ReturnType<typeof setInterval> | null = null;
let engagementCursor = 0;
let lastActivityAt = 0;
let pageVisible = true;
let observers: PerformanceObserver[] = [];
let largestContentfulPaint = 0;
let cumulativeLayoutShift = 0;
let interactionNextPaint = 0;
let reportedLargestContentfulPaint = 0;
let reportedCumulativeLayoutShift = 0;
let reportedInteractionNextPaint = 0;
let documentNavigationReported = false;

const ownHost = (hostname: string): boolean =>
  hostname === window.location.hostname ||
  hostname === "aiinterviewagents.com" ||
  hostname.endsWith(".aiinterviewagents.com");

const HR_ROUTE_ROOTS = [
  "/recruiters", "/interview-questions", "/compare", "/industries",
  "/integrations", "/security", "/languages", "/de", "/fr", "/es", "/sv", "/it",
];

const audienceForRoute = (pathname: string, search: string): Audience | undefined => {
  if (pathname === "/auth/login" || pathname === "/auth/register") {
    const type = new URLSearchParams(search).get("type");
    if (type === "hr" || type === "candidate") return type;
  }
  // The native candidate pages (/es/features/interview-warmup) sit under a recruiter
  // language root but are candidate pages, like their English originals.
  if (parseNativeCandidatePath(pathname)) return undefined;
  if (HR_ROUTE_ROOTS.some((root) => pathname === root || pathname.startsWith(`${root}/`))) return "hr";
  return undefined;
};

const normalizedLabel = (element: Element): string =>
  (element.getAttribute("aria-label") || element.textContent || "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const isConsentUi = (element: Element): boolean => {
  // The attribute marks consent controls whatever language their label is in.
  if (element.closest('[data-consent-ui], [aria-label="Cookie preferences"]')) return true;
  const label = normalizedLabel(element);
  return label === "cookie settings" || label === "accept analytics" || label === "reject optional";
};

const stableRole = (element: Element): string => {
  const explicit = element.getAttribute("role");
  if (explicit === "button" || explicit === "link" || explicit === "menuitem") return explicit;
  if (element instanceof HTMLAnchorElement) return "link";
  if (element instanceof HTMLButtonElement || element instanceof HTMLInputElement) return "button";
  if (element instanceof HTMLFormElement) return "form";
  return "control";
};

const stableType = (element: Element): string => {
  if (element instanceof HTMLAnchorElement) return "link";
  if (element instanceof HTMLFormElement) return "form";
  if (element instanceof HTMLButtonElement || element instanceof HTMLInputElement) {
    return ["button", "submit", "reset"].includes(element.type) ? element.type : "button";
  }
  return "control";
};

const stableTag = (element: Element): string => {
  const tag = element.tagName.toLowerCase();
  return ["a", "button", "input", "form", "div", "span"].includes(tag) ? tag : "element";
};

const elementOrdinal = (element: Element, tag: string): number => {
  const candidates = Array.from(document.querySelectorAll(tag === "element" ? "*" : tag));
  const index = candidates.indexOf(element);
  return Math.max(1, index + 1);
};

const safeTarget = (element: Element): string => {
  const explicit = element.getAttribute("data-analytics-id");
  if (explicit && SAFE_IDENTIFIER.test(explicit)) return explicit;
  const curated = CURATED_LABELS.get(normalizedLabel(element));
  if (curated) return curated;
  const tag = stableTag(element);
  return `${tag}_${stableRole(element)}_${stableType(element)}_${elementOrdinal(element, tag)}`.slice(0, 80);
};

const safeFormTarget = (form: HTMLFormElement): string => safeTarget(form);

const markActivity = (): void => {
  const now = Date.now();
  if (lastActivityAt && now > lastActivityAt + ACTIVE_WINDOW_MS) engagementCursor = now;
  lastActivityAt = now;
};

const flushEngagement = (): void => {
  if (!active || !pageVisible || !pageViewId) return;
  const now = Date.now();
  const activeUntil = Math.min(now, lastActivityAt + ACTIVE_WINDOW_MS);
  const delta = Math.min(ACTIVE_WINDOW_MS, Math.max(0, activeUntil - engagementCursor));
  engagementCursor = now;
  if (delta > 0) {
    trackWebsiteAction("engagement", { metric: "active_ms", value: Math.round(delta) });
  }
};

const reportNavigationPerformance = (): void => {
  if (documentNavigationReported || !active || !pageViewId) return;
  const navigation = performance.getEntriesByType("navigation")[0];
  if (!navigation || !Number.isFinite(navigation.duration) || navigation.duration < 0) return;
  documentNavigationReported = true;
  trackWebsiteAction("performance", {
    metric: "navigation_duration",
    value: Math.min(86_400_000, navigation.duration),
  });
};

const flushPerformance = (): void => {
  if (!active || !pageViewId) return;
  if (largestContentfulPaint > reportedLargestContentfulPaint) {
    reportedLargestContentfulPaint = largestContentfulPaint;
    trackWebsiteAction("performance", {
      metric: "largest_contentful_paint",
      value: Math.min(86_400_000, largestContentfulPaint),
    });
  }
  if (cumulativeLayoutShift > reportedCumulativeLayoutShift) {
    reportedCumulativeLayoutShift = cumulativeLayoutShift;
    trackWebsiteAction("performance", {
      metric: "cumulative_layout_shift",
      value: Math.min(86_400_000, cumulativeLayoutShift),
    });
  }
  if (interactionNextPaint > reportedInteractionNextPaint) {
    reportedInteractionNextPaint = interactionNextPaint;
    trackWebsiteAction("performance", {
      metric: "interaction_next_paint",
      value: Math.min(86_400_000, interactionNextPaint),
    });
  }
};

const resetPageSignals = (): void => {
  startedForms = new WeakSet<HTMLFormElement>();
  scrollMarks = new Set<number>();
  rageTargets = new Map<string, RageState>();
  signupStartedEmitted = false;
  audienceEmitted = undefined;
  engagementCursor = Date.now();
  lastActivityAt = engagementCursor;
  pageVisible = document.visibilityState !== "hidden";
  largestContentfulPaint = 0;
  cumulativeLayoutShift = 0;
  interactionNextPaint = 0;
  reportedLargestContentfulPaint = 0;
  reportedCumulativeLayoutShift = 0;
  reportedInteractionNextPaint = 0;
};

const emitPageMilestones = (): void => {
  if (!active || !pageViewId) return;
  if (pageAudience && audienceEmitted !== pageAudience) {
    audienceEmitted = pageAudience;
    trackWebsiteAction("audience_selected", { audience: pageAudience });
  }
  if (pageHint?.template === "/auth/register" && !signupStartedEmitted) {
    signupStartedEmitted = true;
    trackWebsiteAction("signup_started", pageAudience ? { audience: pageAudience } : {});
  }
};

const syncPage = (): void => {
  const context = getWebsiteTrackingContext();
  if (!active || !context) return;
  if (context.pageViewId !== pageViewId) {
    pageViewId = context.pageViewId;
    pageAudience = pageHint?.audience;
    resetPageSignals();
  } else if (pageHint?.audience !== pageAudience) {
    pageAudience = pageHint?.audience;
  }
  emitPageMilestones();
  reportNavigationPerformance();
};

const onClick = (event: MouseEvent): void => {
  if (!(event.target instanceof Element)) return;
  const control = event.target.closest(INTERACTIVE_SELECTOR);
  if (!control || isConsentUi(control)) return;
  markActivity();

  const target = safeTarget(control);
  const anchor = control.closest("a[href]") as HTMLAnchorElement | null;
  let action: "click" | "outbound_click" | "download_click" = "click";
  let targetPath: string | undefined;
  let targetHost: string | undefined;
  let targetContent: string | undefined;
  let linkAudience: Audience | undefined;
  let linkIsRegistration = false;

  if (anchor) {
    try {
      const url = new URL(anchor.href, window.location.href);
      if (/^https?:$/.test(url.protocol) && !ownHost(url.hostname.toLowerCase())) {
        action = "outbound_click";
        targetHost = url.hostname.toLowerCase();
        // Our own Just One Interview links name the AIIA page they sit on; other sites' query strings stay unread.
        if (targetHost === "justoneinterview.com" || targetHost.endsWith(".justoneinterview.com")) {
          targetContent = url.searchParams.get("utm_content") ?? undefined;
        }
      } else if (anchor.hasAttribute("download") || DOWNLOAD_EXTENSION.test(url.pathname)) {
        action = "download_click";
        targetPath = routeTemplateForPath(url.pathname);
      } else if (/^https?:$/.test(url.protocol)) {
        targetPath = routeTemplateForPath(url.pathname);
      }
      if (ownHost(url.hostname.toLowerCase())) {
        linkAudience = audienceForRoute(url.pathname, url.search);
        linkIsRegistration = url.pathname === "/auth/register";
      }
    } catch {
      targetPath = undefined;
    }
  }

  trackWebsiteAction(action, {
    target: action === "outbound_click" ? "external_link" : target,
    ...(targetPath ? { target_path: targetPath } : {}),
    ...(targetHost ? { target_host: targetHost } : {}),
    ...(targetContent ? { target_content: targetContent } : {}),
  });
  if (linkAudience) trackWebsiteAction("audience_selected", { audience: linkAudience });
  if (linkIsRegistration || target === "sign_up" || target === "start_free") {
    trackWebsiteAction("signup_started", linkAudience ? { audience: linkAudience } : {});
  }

  const now = Date.now();
  const prior = rageTargets.get(target);
  const next = prior && now - prior.startedAt <= RAGE_WINDOW_MS
    ? { count: prior.count + 1, startedAt: prior.startedAt }
    : { count: 1, startedAt: now };
  if (next.count >= 3) {
    trackWebsiteAction("rage_click", { target });
    rageTargets.delete(target);
  } else {
    rageTargets.set(target, next);
  }
};

const onFocusIn = (event: FocusEvent): void => {
  if (!(event.target instanceof Element)) return;
  markActivity();
  const form = event.target.closest("form");
  if (!form || isConsentUi(form) || startedForms.has(form)) return;
  startedForms.add(form);
  trackWebsiteAction("form_start", { target: safeFormTarget(form) });
};

const onSubmit = (event: SubmitEvent): void => {
  if (!(event.target instanceof HTMLFormElement) || isConsentUi(event.target)) return;
  markActivity();
  trackWebsiteAction("form_submit", { target: safeFormTarget(event.target) });
};

const onInvalid = (event: Event): void => {
  if (!(event.target instanceof Element)) return;
  const form = event.target.closest("form");
  if (!form || isConsentUi(form)) return;
  trackWebsiteAction("form_invalid", { target: safeFormTarget(form) });
};

const onScroll = (): void => {
  markActivity();
  if (scrollFrame) return;
  scrollFrame = window.requestAnimationFrame(() => {
    scrollFrame = 0;
    const root = document.documentElement;
    const scrollable = Math.max(0, root.scrollHeight - window.innerHeight);
    if (!scrollable) return;
    const percent = Math.min(100, Math.max(0, (window.scrollY / scrollable) * 100));
    for (const mark of [25, 50, 75, 100]) {
      if (percent >= mark && !scrollMarks.has(mark)) {
        scrollMarks.add(mark);
        trackWebsiteAction("scroll", { metric: "depth_percent", value: mark });
      }
    }
  });
};

const errorClass = (error: unknown, fallback: string): string => {
  if (error instanceof Error) return ERROR_CLASSES.get(error.name) || "error";
  return fallback;
};

// "page-3f9a.js:1:2345": the script file name and position, without its address.
const errorSource = (url: string, line?: number, column?: number): string | undefined => {
  const file = url.split(/[?#]/)[0].split("/").pop();
  if (!file) return undefined;
  return line ? `${file}:${line}:${column || 0}` : file;
};

const stackSource = (error: unknown): string | undefined => {
  if (!(error instanceof Error) || !error.stack) return undefined;
  const frame = error.stack.match(/([^\s()@]+\.m?js)(?:\?[^\s():]*)?:(\d+):(\d+)/);
  return frame ? errorSource(frame[1], Number(frame[2]), Number(frame[3])) : undefined;
};

const errorMessage = (error: unknown): string | undefined =>
  error instanceof Error ? error.message : typeof error === "string" ? error : undefined;

const onError = (event: ErrorEvent): void => {
  trackWebsiteAction("error", {
    target: errorClass(event.error, "script_error"),
    error_message: errorMessage(event.error) || event.message,
    error_source: event.filename
      ? errorSource(event.filename, event.lineno, event.colno)
      : stackSource(event.error),
  });
};

const onUnhandledRejection = (event: PromiseRejectionEvent): void => {
  trackWebsiteAction("error", {
    target: errorClass(event.reason, "unhandled_rejection"),
    error_message: errorMessage(event.reason),
    error_source: stackSource(event.reason),
  });
};

const onOnline = (): void => trackWebsiteAction("product_event", { target: "network_online" });
const onOffline = (): void => trackWebsiteAction("product_event", { target: "network_offline" });

const onVisibilityChange = (): void => {
  if (document.visibilityState === "hidden") {
    flushEngagement();
    flushPerformance();
    pageVisible = false;
    return;
  }
  pageVisible = true;
  engagementCursor = Date.now();
};

const onPageHide = (): void => {
  flushEngagement();
  flushPerformance();
};

const observePerformance = (): void => {
  if (typeof PerformanceObserver === "undefined") return;
  const supported = new Set(PerformanceObserver.supportedEntryTypes || []);
  const addObserver = (type: string, callback: (entries: PerformanceEntry[]) => void): void => {
    if (!supported.has(type)) return;
    try {
      const observer = new PerformanceObserver((list) => callback(list.getEntries()));
      if (type === "event") {
        observer.observe({ type, buffered: true, durationThreshold: 40 } as PerformanceObserverInit);
      } else {
        observer.observe({ type, buffered: true });
      }
      observers.push(observer);
    } catch {
      /* Unsupported performance entry options are optional. */
    }
  };
  addObserver("largest-contentful-paint", (entries) => {
    const last = entries.at(-1);
    if (last) largestContentfulPaint = Math.max(largestContentfulPaint, last.startTime);
  });
  addObserver("layout-shift", (entries) => {
    for (const entry of entries as Array<PerformanceEntry & { value?: number; hadRecentInput?: boolean }>) {
      if (!entry.hadRecentInput && typeof entry.value === "number" && Number.isFinite(entry.value)) {
        cumulativeLayoutShift += Math.max(0, entry.value);
      }
    }
  });
  addObserver("event", (entries) => {
    for (const entry of entries as Array<PerformanceEntry & { duration?: number; interactionId?: number }>) {
      if (entry.interactionId && typeof entry.duration === "number" && Number.isFinite(entry.duration)) {
        interactionNextPaint = Math.max(interactionNextPaint, entry.duration);
      }
    }
  });
};

const addListeners = (): void => {
  document.addEventListener("click", onClick, true);
  document.addEventListener("focusin", onFocusIn);
  document.addEventListener("submit", onSubmit);
  document.addEventListener("invalid", onInvalid, true);
  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("pointerdown", markActivity, { passive: true });
  window.addEventListener("keydown", markActivity);
  window.addEventListener("touchstart", markActivity, { passive: true });
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onUnhandledRejection);
  window.addEventListener("online", onOnline);
  window.addEventListener("offline", onOffline);
  window.addEventListener("load", reportNavigationPerformance);
  window.addEventListener("pagehide", onPageHide);
  document.addEventListener("visibilitychange", onVisibilityChange);
};

const removeListeners = (): void => {
  document.removeEventListener("click", onClick, true);
  document.removeEventListener("focusin", onFocusIn);
  document.removeEventListener("submit", onSubmit);
  document.removeEventListener("invalid", onInvalid, true);
  window.removeEventListener("scroll", onScroll);
  window.removeEventListener("pointerdown", markActivity);
  window.removeEventListener("keydown", markActivity);
  window.removeEventListener("touchstart", markActivity);
  window.removeEventListener("error", onError);
  window.removeEventListener("unhandledrejection", onUnhandledRejection);
  window.removeEventListener("online", onOnline);
  window.removeEventListener("offline", onOffline);
  window.removeEventListener("load", reportNavigationPerformance);
  window.removeEventListener("pagehide", onPageHide);
  document.removeEventListener("visibilitychange", onVisibilityChange);
};

const activate = (): void => {
  if (active || !installed || !hasAnalyticsConsent()) return;
  active = true;
  addListeners();
  observePerformance();
  engagementTimer = setInterval(flushEngagement, ENGAGEMENT_INTERVAL_MS);
  syncPage();
};

const deactivate = (flushFinal: boolean): void => {
  if (!active) return;
  if (flushFinal && hasAnalyticsConsent()) {
    flushEngagement();
    flushPerformance();
  }
  active = false;
  removeListeners();
  if (scrollFrame) cancelAnimationFrame(scrollFrame);
  scrollFrame = 0;
  if (engagementTimer) clearInterval(engagementTimer);
  engagementTimer = null;
  observers.forEach((observer) => observer.disconnect());
  observers = [];
  pageViewId = "";
  pageAudience = undefined;
  resetPageSignals();
};

export const setWebsiteActivityPage = (pathname: string, search = ""): void => {
  pageHint = {
    template: routeTemplateForPath(pathname),
    audience: audienceForRoute(pathname, search),
  };
  syncPage();
};

export const flushWebsiteActivityPage = (): void => {
  if (!active) return;
  flushEngagement();
  flushPerformance();
};

export const startWebsiteActivity = (): void => {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  subscribers += 1;
  if (installed) return;
  installed = true;
  unsubscribeConsent = subscribeToAnalyticsConsent((consent) => {
    if (consent === "accepted") activate();
    else deactivate(false);
  });
  activate();
};

export const stopWebsiteActivity = (): void => {
  subscribers = Math.max(0, subscribers - 1);
  if (subscribers || !installed) return;
  deactivate(true);
  unsubscribeConsent?.();
  unsubscribeConsent = null;
  installed = false;
  pageHint = null;
};
